export const DIY_MARKETPLACE_OPTIONS = [
  { code: "xianyu", label: "Xianyu" },
  { code: "yupoo", label: "Yupoo" },
  { code: "xiaohongshu", label: "Xiaohongshu" },
  { code: "jd", label: "JD.com" },
  { code: "taobao", label: "Taobao" },
  { code: "1688", label: "1688" },
] as const;

export const DIY_SELLER_PROOF_OPTIONS = [
  { code: "seller-chat", label: "Seller chat" },
  { code: "screenshot", label: "Screenshot" },
  { code: "wechat-seller", label: "WeChat seller" },
  { code: "offline-shop", label: "Offline shop" },
] as const;

export const DIY_ATTACHMENT_POLICY = {
  enabled: false,
  maximumCount: 5,
  maximumBytesEach: 8 * 1024 * 1024,
  allowedMimeTypes: ["image/jpeg", "image/png", "image/webp"],
  requiredSecurityState: "clean",
} as const;

export type DiyMarketplace = (typeof DIY_MARKETPLACE_OPTIONS)[number]["code"];
export type DiySellerProof = (typeof DIY_SELLER_PROOF_OPTIONS)[number]["code"];
export type DiyRequestStatus = "draft" | "submitted" | "review-required" | "rejected" | "approved-for-order";

export type DiyMoneyInput = {
  amountMinor: number;
  currency: "CNY";
  precision: 2;
};

export type DiyCustomerInputMoney = DiyMoneyInput & {
  authority: "customer-supplied-for-review";
};

export type DiyAttachmentMetadata = {
  attachmentId: string;
  originalName: string;
  declaredMimeType: (typeof DIY_ATTACHMENT_POLICY.allowedMimeTypes)[number];
  sizeBytes: number;
  checksumSha256: string;
};

export type DiyRequestPayload = {
  source: {
    url?: string;
    marketplace: DiyMarketplace;
    sellerProof: DiySellerProof[];
    description?: string;
  };
  product: {
    name?: string;
    specification?: string;
    quantity: number;
  };
  pricing:
    | {
        mode: "known-seller-price";
        sellerTotal: DiyMoneyInput;
        domesticShipping: DiyMoneyInput;
      }
    | {
        mode: "request-price-confirmation";
        sellerTotal?: never;
        domesticShipping?: never;
      };
  notes?: string;
  attachments: DiyAttachmentMetadata[];
};

export type ValidatedDiyRequestPayload = Omit<DiyRequestPayload, "pricing"> & {
  pricing:
    | {
        mode: "known-seller-price";
        sellerTotal: DiyCustomerInputMoney;
        domesticShipping: DiyCustomerInputMoney;
      }
    | { mode: "request-price-confirmation" };
};

export type DiyRequestRecordDto = {
  id: string;
  status: DiyRequestStatus;
  submittedAt: string | null;
  updatedAt: string;
  customerPriceAuthority: false;
  orderId: null;
  paymentId: null;
};

export type DiyRequestCapability = {
  create: false;
  persistence: false;
  attachments: {
    upload: false;
    reason: "DIY_ATTACHMENT_UPLOAD_UNAVAILABLE";
    policy: typeof DIY_ATTACHMENT_POLICY;
  };
  deposit: { available: false; reason: "DIY_DEPOSIT_UNAVAILABLE" };
  payment: { available: false; reason: "DIY_PAYMENT_UNAVAILABLE" };
  orderCreation: { available: false; reason: "DIY_ORDER_HANDOFF_UNAVAILABLE" };
};

export type DiyRequestApiResponse =
  | { ok: true; request: DiyRequestRecordDto; capability: DiyRequestCapability }
  | {
      ok: false;
      error: string;
      displayMessage?: string;
      issues?: Array<{ field: string; code: string }>;
      capability?: DiyRequestCapability;
    };

export const DIY_REQUEST_STATUS_TRANSITIONS: Readonly<Record<DiyRequestStatus, readonly DiyRequestStatus[]>> = {
  draft: ["submitted"],
  submitted: ["review-required"],
  "review-required": ["rejected", "approved-for-order"],
  rejected: [],
  "approved-for-order": [],
};
